<template>
  <div v-if="isOpen" class="popup-overlay" @click="closeModal">
    <div class="popup-content" @click.stop>
      <div class="popup-header">
        <span class="popup-title">{{ $t('main.traffic.trafficShareTitle') }}</span>
        <img :src="CloseIcon" class="close-icon" @click="closeModal" />
      </div>

      <div class="controls-container">
        <div class="controls-left">
          <button
            @click="toggleAnimation"
            class="animation-button"
            :disabled="!hasDataForAnimation || isLatestMonth"
            :title="isLatestMonth ? $t('main.traffic.noDataToAnimate') : (isAnimating ? $t('main.traffic.pauseAnimation') : $t('main.traffic.playAnimation'))"
          >
            <img :src="isAnimating ? '/pause_icon.svg' : '/play_icon.svg'" alt="Play/Pause" />
          </button>

          <button
            v-if="isAnimating"
            @click="cycleSpeed"
            class="speed-button"
            :title="$t('main.traffic.animationSpeed') || 'Animation speed'"
          >
            {{ speedLabel }}
          </button>
        </div>

        <div class="date-display" v-if="isAnimating">
          <span class="animation-date">
            {{ animationDateDisplay }}
          </span>
        </div>

        <div class="date-selector" v-if="!isAnimating">
          <select v-model="selectedYear" @change="onDateChange" class="year-select">
            <option v-for="year in availableYears" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
          <select v-model="selectedMonth" @change="onDateChange" class="month-select">
            <option v-for="(month, index) in monthNames" :key="index" :value="index">
              {{ month }}
            </option>
          </select>
        </div>
      </div>

      <div class="chart-container">
        <canvas ref="pieCanvas" style="display: none;"></canvas>
        <div v-if="chartData.length === 0 && !isAnimating" class="no-data">
          {{ $t('main.traffic.noDataForPeriod') || 'No data available for this period' }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { mapGetters, mapState } from 'vuex'
import getColor from '@/helpers/store/getColor'
import nFormatter from '@/helpers/processing/nFormatter'
import CloseIcon from '@/assets/icons/base/close.svg'
import { cssVar, onThemeChange } from '@/helpers/base/theme'

export default {
  name: 'PieChartModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      CloseIcon,
      chart: null,
      selectedYear: null,
      selectedMonth: null,
      chartData: [],
      isDestroying: false,
      debugMode: false, // Debug logging disabled
      // Animation properties
      isAnimating: false,
      animationFrame: null,
      animationStartTime: null,
      animationCurrentDate: null,
      animationEndDate: null,
      animationDuration: 1000, // 1 second per month
      animationSpeed: 1, // Speed multiplier: 1, 2, or 3
      animationProgress: 0,
      interpolatedData: [],
      lastUpdateTime: 0,
      updateThrottle: 16, // Update chart at most every 16ms (~60 FPS) for smooth animation
      currentAnimationMonth: -1, // Track current month to avoid duplicate updates
      hiddenDomains: [], // Track which domains are hidden via legend
      domainColorMap: {}, // Fixed color mapping for all domains
    }
  },
  computed: {
    ...mapGetters(['domains']),
    ...mapState('traffic', {
      trafficData: state => state.data,
    }),
    monthNames() {
      return [
        this.$t('months.january') || 'January',
        this.$t('months.february') || 'February',
        this.$t('months.march') || 'March',
        this.$t('months.april') || 'April',
        this.$t('months.may') || 'May',
        this.$t('months.june') || 'June',
        this.$t('months.july') || 'July',
        this.$t('months.august') || 'August',
        this.$t('months.september') || 'September',
        this.$t('months.october') || 'October',
        this.$t('months.november') || 'November',
        this.$t('months.december') || 'December',
      ]
    },
    availableYears() {
      if (!this.trafficData || this.trafficData.length === 0) return []

      const years = new Set()
      this.trafficData.forEach(domainData => {
        Object.keys(domainData.data).forEach(dateStr => {
          const year = new Date(dateStr).getFullYear()
          years.add(year)
        })
      })

      return Array.from(years).sort((a, b) => b - a)
    },
    hasDataForAnimation() {
      return this.trafficData && this.trafficData.length > 0
    },
    animationDateDisplay() {
      if (!this.animationCurrentDate) return ''
      const month = this.monthNames[this.animationCurrentDate.getMonth()]
      const year = this.animationCurrentDate.getFullYear()
      return `${month} ${year}`
    },
    isLatestMonth() {
      // Check if the selected month is the latest available month
      if (!this.trafficData || this.trafficData.length === 0) return true

      // Find the latest date in the data
      let latestDate = null
      this.trafficData.forEach(domainData => {
        Object.keys(domainData.data).forEach(dateStr => {
          const date = new Date(dateStr)
          if (!latestDate || date > latestDate) {
            latestDate = date
          }
        })
      })

      if (!latestDate) return true

      // Check if selected year and month match the latest date
      return this.selectedYear === latestDate.getFullYear() &&
             this.selectedMonth === latestDate.getMonth()
    },
    speedLabel() {
      return `×${this.animationSpeed}`
    },
  },
  watch: {
    isOpen(newVal) {
      if (this.debugMode) console.log('[PieChart] Modal isOpen changed:', newVal)

      if (newVal) {
        // Reset animation state
        this.stopAnimation()
        // Reset animation speed to x1
        this.animationSpeed = 1
        // Reset hidden domains when modal opens
        this.hiddenDomains = []
        // Reset color map to be re-established
        this.domainColorMap = {}
        // Reset destroying flag
        this.isDestroying = false
        // Clean up existing chart first
        this.destroyChart()
        this.initializeData()
        // Double nextTick to ensure DOM is fully ready
        this.$nextTick(() => {
          this.$nextTick(() => {
            if (!this.isDestroying) {
              this.renderChart()
            }
          })
        })
      } else {
        // Stop animation when closing
        this.stopAnimation()
        // Ensure cleanup when closing
        this.destroyChart()
        // Hide canvas when modal closes
        if (this.$refs.pieCanvas) {
          this.$refs.pieCanvas.style.display = 'none'
        }
      }
    },
  },
  methods: {
    destroyChart() {
      if (this.debugMode) console.log('[PieChart] destroyChart called, chart exists:', !!this.chart)

      if (this.chart) {
        this.isDestroying = true
        try {
          // Disable all animations and interactions immediately
          if (this.chart.options) {
            this.chart.options.animation = false
            this.chart.options.animations = false
            this.chart.options.transitions = false
            this.chart.options.interaction = { enabled: false }
          }

          // Stop any pending updates
          if (this.chart.stop) {
            this.chart.stop()
          }

          // Clear any pending animations
          if (this.chart._animationsDisabled !== undefined) {
            this.chart._animationsDisabled = true
          }

          // Clear the canvas before destroying
          if (this.chart.canvas) {
            const ctx = this.chart.canvas.getContext('2d')
            if (ctx) {
              ctx.clearRect(0, 0, this.chart.canvas.width, this.chart.canvas.height)
            }
          }

          // Destroy the chart
          this.chart.destroy()
          if (this.debugMode) console.log('[PieChart] Chart destroyed successfully')
        } catch (e) {
          console.error('[PieChart] Error destroying chart:', e)
        } finally {
          this.chart = null
          this.isDestroying = false
        }
      }
    },
    closeModal() {
      this.$emit('close-modal')
    },
    cycleSpeed() {
      // Cycle through speeds: 1 -> 2 -> 3 -> 1
      this.animationSpeed = this.animationSpeed === 3 ? 1 : this.animationSpeed + 1
    },
    toggleAnimation() {
      if (this.isAnimating) {
        this.pauseAnimation()
      } else {
        this.startAnimation()
      }
    },
    startAnimation() {
      if (!this.hasDataForAnimation) return

      // Reset animation tracking variables
      this.lastUpdateTime = 0
      this.currentAnimationMonth = -1

      // Ensure chart exists before starting animation
      if (!this.chart) {
        // Create initial chart if it doesn't exist
        this.calculateChartData()
        if (this.chartData.length > 0) {
          this.$nextTick(() => {
            if (this.$refs.pieCanvas) {
              const ctx = this.$refs.pieCanvas.getContext('2d')
              if (ctx) {
                this.$refs.pieCanvas.style.display = 'block'
                this.createChart(ctx)
                // Start animation after chart is created
                this.$nextTick(() => {
                  this.startAnimationInternal()
                })
              }
            }
          })
        }
        return
      }

      this.startAnimationInternal()
    },
    startAnimationInternal() {
      // Find earliest and latest dates
      const allDates = []
      this.trafficData.forEach(domainData => {
        Object.keys(domainData.data).forEach(dateStr => {
          allDates.push(new Date(dateStr))
        })
      })
      allDates.sort((a, b) => a - b)

      if (allDates.length === 0) return

      // Set animation range from current selected date to latest date
      const startDate = new Date(this.selectedYear, this.selectedMonth, 1)
      const latestDate = allDates[allDates.length - 1]

      // Calculate total months to animate
      const monthsDiff = (latestDate.getFullYear() - startDate.getFullYear()) * 12 +
                        (latestDate.getMonth() - startDate.getMonth()) + 1

      if (monthsDiff <= 0) return

      this.animationCurrentDate = new Date(startDate)
      this.animationEndDate = new Date(latestDate.getFullYear(), latestDate.getMonth() + 1, 0)
      this.animationStartTime = Date.now()
      this.isAnimating = true

      // Start animation loop
      this.animate()
    },
    pauseAnimation() {
      this.isAnimating = false
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame)
        this.animationFrame = null
      }
    },
    stopAnimation() {
      this.pauseAnimation()
      this.animationCurrentDate = null
      this.animationProgress = 0
    },
    animate() {
      if (!this.isAnimating) return

      const now = Date.now()
      const elapsed = now - this.animationStartTime

      // Calculate current month based on elapsed time (adjusted by speed)
      const startDate = new Date(this.selectedYear, this.selectedMonth, 1)
      const totalMonths = (this.animationEndDate.getFullYear() - startDate.getFullYear()) * 12 +
                         (this.animationEndDate.getMonth() - startDate.getMonth()) + 1

      const monthProgress = (elapsed * this.animationSpeed) / this.animationDuration
      const currentMonthIndex = Math.floor(monthProgress)
      const monthFraction = monthProgress - currentMonthIndex

      if (currentMonthIndex >= totalMonths - 1) {
        // Animation complete
        this.animationCurrentDate = new Date(this.animationEndDate)
        this.calculateAnimatedData(this.animationCurrentDate, 0)

        // Update the selected date to match the final animation date
        this.selectedYear = this.animationEndDate.getFullYear()
        this.selectedMonth = this.animationEndDate.getMonth()

        this.stopAnimation()
        return
      }

      // Calculate current animated date
      const currentDate = new Date(
        startDate.getFullYear(),
        startDate.getMonth() + currentMonthIndex,
        1
      )
      this.animationCurrentDate = currentDate

      // Only update chart if enough time has passed since last update
      // or if we're in a new month
      const shouldUpdate = (now - this.lastUpdateTime > this.updateThrottle) ||
                          (currentMonthIndex !== this.currentAnimationMonth)

      if (shouldUpdate) {
        this.currentAnimationMonth = currentMonthIndex
        this.lastUpdateTime = now
        // Calculate and render interpolated data
        this.calculateAnimatedData(currentDate, monthFraction)
      }

      // Continue animation
      this.animationFrame = requestAnimationFrame(() => this.animate())
    },
    calculateAnimatedData(baseDate, fraction) {
      const year = baseDate.getFullYear()
      const month = baseDate.getMonth()

      // Get data for current and next month
      const currentMonthData = this.getMonthData(year, month)
      const nextMonthData = fraction > 0 ? this.getMonthData(
        month === 11 ? year + 1 : year,
        month === 11 ? 0 : month + 1
      ) : null

      // Interpolate between months if needed
      if (nextMonthData && fraction > 0) {
        this.chartData = this.interpolateData(currentMonthData, nextMonthData, fraction)
      } else {
        this.chartData = currentMonthData
      }

      // Update chart with new data
      this.updateChartData()
    },
    getMonthData(year, month) {
      const startDate = new Date(year, month, 1)
      const endDate = new Date(year, month + 1, 0)
      const domainTotals = {}

      // Calculate totals for the month
      this.trafficData.forEach((domainData) => {
        // Skip hidden domains
        if (this.hiddenDomains.includes(domainData.domainName)) {
          return
        }

        let total = 0
        Object.keys(domainData.data).forEach(dateStr => {
          const date = new Date(dateStr)
          if (date >= startDate && date <= endDate) {
            const value = domainData.data[dateStr]
            if (value !== null && value !== undefined) {
              total += value
            }
          }
        })

        if (total > 0) {
          domainTotals[domainData.domainName] = total
        }
      })

      // Convert to chart data format with fixed colors
      return Object.entries(domainTotals).map(([domain, total]) => ({
        domain,
        total,
        color: this.domainColorMap[domain] || getColor(0)
      }))
    },
    interpolateData(data1, data2, fraction) {
      // Create a map of all domains
      const allDomains = new Set([
        ...data1.map(d => d.domain),
        ...data2.map(d => d.domain)
      ])

      const interpolated = []

      allDomains.forEach(domain => {
        const item1 = data1.find(d => d.domain === domain)
        const item2 = data2.find(d => d.domain === domain)

        const value1 = item1 ? item1.total : 0
        const value2 = item2 ? item2.total : 0

        // Smoother interpolation using cubic ease-in-out
        const easedFraction = fraction < 0.5
          ? 4 * fraction * fraction * fraction
          : 1 - Math.pow(-2 * fraction + 2, 3) / 2

        const interpolatedValue = value1 + (value2 - value1) * easedFraction

        if (interpolatedValue > 0) {
          interpolated.push({
            domain,
            total: interpolatedValue,
            color: this.domainColorMap[domain] || getColor(0)
          })
        }
      })

      return interpolated
    },
    updateChartData() {
      if (!this.chartData || this.chartData.length === 0) return

      // If chart doesn't exist, create it
      if (!this.chart) {
        if (this.$refs.pieCanvas) {
          const ctx = this.$refs.pieCanvas.getContext('2d')
          if (ctx) {
            this.$refs.pieCanvas.style.display = 'block'
            if (this.isAnimating) {
              this.createChartForAnimation(ctx)
            } else {
              this.createChart(ctx)
            }
          }
        }
        return
      }

      // Update existing chart data without recreating (prevents tooltip flicker)
      try {
        const labels = this.chartData.map(item => item.domain)
        const data = this.chartData.map(item => item.total)
        const colors = this.chartData.map(item => item.color)
        const totalTraffic = data.reduce((sum, val) => sum + val, 0)

        // Update chart data
        this.chart.data.labels = labels
        this.chart.data.datasets[0].data = data
        this.chart.data.datasets[0].backgroundColor = colors
        this.chart.data.datasets[0].hoverBorderColor = colors

        // Update datalabels formatter with new total
        if (this.chart.options?.plugins?.datalabels) {
          this.chart.options.plugins.datalabels.formatter = (value, ctx) => {
            // Check if this segment is hidden
            const meta = ctx.chart.getDatasetMeta(0)
            if (meta.data[ctx.dataIndex] && meta.data[ctx.dataIndex].hidden) {
              return '' // Don't show label for hidden segments
            }

            // Calculate total of only visible segments
            let visibleTotal = 0
            ctx.dataset.data.forEach((val, idx) => {
              if (!meta.data[idx] || !meta.data[idx].hidden) {
                visibleTotal += val
              }
            })

            const percentage = visibleTotal > 0 ? ((value * 100) / visibleTotal).toFixed(1) : '0'
            return percentage > 5 ? `${percentage}%` : '' // Only show if > 5%
          }
        }

        // Update tooltip callback with new total (if tooltip is enabled)
        if (this.chart.options?.plugins?.tooltip?.callbacks) {
          this.chart.options.plugins.tooltip.callbacks.label = (context) => {
            const value = context.parsed || 0
            const formattedValue = nFormatter(value)
            const domainName = context.label
            const monthYear = this.isAnimating
              ? (this.animationDateDisplay || '')
              : `${this.monthNames[this.selectedMonth]} ${this.selectedYear}`

            // Calculate percentage based on visible segments only
            const meta = context.chart.getDatasetMeta(0)
            let visibleTotal = 0
            context.dataset.data.forEach((val, idx) => {
              if (!meta.data[idx] || !meta.data[idx].hidden) {
                visibleTotal += val
              }
            })
            const percentage = visibleTotal > 0 ? ((value * 100) / visibleTotal).toFixed(1) : '0'

            return [
              domainName,
              monthYear,
              `Visits: ${formattedValue}`,
              `Market Share: ${percentage}%`
            ]
          }
        }

        // Update with 'none' mode to prevent animation and tooltip reset
        this.chart.update('none')
      } catch (e) {
        console.error('[PieChart] Error updating chart:', e)
        // If update fails, recreate the chart
        if (this.chart) {
          try {
            this.chart.destroy()
          } catch (destroyError) {
            // Ignore destroy errors
          }
          this.chart = null
        }
        if (this.$refs.pieCanvas) {
          const ctx = this.$refs.pieCanvas.getContext('2d')
          if (ctx) {
            this.$refs.pieCanvas.style.display = 'block'
            this.createChart(ctx)
          }
        }
      }
    },
    applyThemeColors() {
      const chart = this.chart
      if (!chart) return
      chart.data.datasets.forEach(dataset => {
        dataset.borderColor = cssVar('--chart-segment-border')
      })
      const { tooltip } = chart.options.plugins
      tooltip.backgroundColor = cssVar('--bg-surface')
      tooltip.titleColor = cssVar('--text-primary')
      tooltip.bodyColor = cssVar('--text-primary')
      tooltip.borderColor = cssVar('--border-color-2')
      chart.update('none')
    },
    createChartForAnimation(ctx) {
      // Simplified chart creation for animation with datalabels
      const labels = this.chartData.map(item => item.domain)
      const data = this.chartData.map(item => item.total)
      const colors = this.chartData.map(item => item.color)
      const totalTraffic = data.reduce((sum, val) => sum + val, 0)

      this.chart = new Chart(ctx, {
        type: 'pie',
        data: {
          labels,
          datasets: [{
            data,
            backgroundColor: colors,
            borderColor: cssVar('--chart-segment-border'),
            borderWidth: 2,
          }],
        },
        plugins: [ChartDataLabels], // Add datalabels plugin
        options: {
          responsive: true,
          maintainAspectRatio: true,
          animation: false,
          plugins: {
            datalabels: {
              color: '#fff',
              font: {
                weight: 'bold',
                size: 14,
              },
              formatter: (value, ctx) => {
                // Check if this segment is hidden
                const meta = ctx.chart.getDatasetMeta(0)
                if (meta.data[ctx.dataIndex] && meta.data[ctx.dataIndex].hidden) {
                  return '' // Don't show label for hidden segments
                }

                // Calculate total of only visible segments
                let visibleTotal = 0
                ctx.dataset.data.forEach((val, idx) => {
                  if (!meta.data[idx] || !meta.data[idx].hidden) {
                    visibleTotal += val
                  }
                })

                const percentage = visibleTotal > 0 ? ((value * 100) / visibleTotal).toFixed(1) : '0'
                return percentage > 5 ? `${percentage}%` : '' // Only show if > 5%
              },
              display: true,
              anchor: 'center',
              align: 'center',
              offset: 0,
            },
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                generateLabels: (chart) => {
                  const data = chart.data
                  if (data.labels.length && data.datasets.length) {
                    const meta = chart.getDatasetMeta(0)
                    return data.labels.map((label, i) => {
                      const style = meta.controller.getStyle(i)
                      const isHidden = meta.data[i] && meta.data[i].hidden
                      return {
                        text: label,
                        fillStyle: isHidden ? cssVar('--chart-hidden') : style.backgroundColor,
                        strokeStyle: style.borderColor,
                        lineWidth: style.borderWidth,
                        hidden: isHidden,
                        index: i,
                        // Add custom properties for styling
                        fontColor: isHidden ? cssVar('--chart-hidden') : cssVar('--text-secondary2'),
                        textDecoration: isHidden ? 'line-through' : ''
                      }
                    })
                  }
                  return []
                },
                color: function(context) {
                  // Use the fontColor property if available
                  return context.fontColor || cssVar('--text-secondary2')
                },
                font: {
                  family: 'Manrope',
                  size: 12,
                },
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 15,
              },
              onClick: (e, legendItem, legend) => {
                const index = legendItem.index
                const chart = legend.chart
                const meta = chart.getDatasetMeta(0)
                const domain = legendItem.text

                // Toggle visibility manually
                if (meta && meta.data && meta.data[index]) {
                  // Toggle the hidden state
                  meta.data[index].hidden = !meta.data[index].hidden

                  // Update hidden domains list
                  if (meta.data[index].hidden) {
                    if (!this.hiddenDomains.includes(domain)) {
                      this.hiddenDomains.push(domain)
                    }
                  } else {
                    this.hiddenDomains = this.hiddenDomains.filter(d => d !== domain)
                  }

                  // Update the chart
                  chart.update('none')
                }
              },
            },
            tooltip: {
              enabled: true, // Keep tooltip enabled for additional info
              backgroundColor: cssVar('--bg-surface'),
              titleColor: cssVar('--text-primary'),
              bodyColor: cssVar('--text-primary'),
              borderColor: cssVar('--border-color-2'),
              borderWidth: 1,
              padding: 12,
              cornerRadius: 7,
              displayColors: false,
              callbacks: {
                title: () => '', // Hide redundant title
                label: (context) => {
                  const value = context.parsed || 0
                  const formattedValue = nFormatter(value)
                  const domainName = context.label
                  const monthYear = this.animationDateDisplay || ''

                  // Calculate percentage based on visible segments only
                  const meta = context.chart.getDatasetMeta(0)
                  let visibleTotal = 0
                  context.dataset.data.forEach((val, idx) => {
                    if (!meta.data[idx] || !meta.data[idx].hidden) {
                      visibleTotal += val
                    }
                  })
                  const percentage = visibleTotal > 0 ? ((value * 100) / visibleTotal).toFixed(1) : '0'

                  return [
                    domainName,
                    monthYear,
                    `Visits: ${formattedValue}`,
                    `Market Share: ${percentage}%`
                  ]
                },
              },
            },
          },
        },
      })
    },
    initializeData() {
      // Find the latest date among all domains
      let latestDate = null

      if (this.trafficData && this.trafficData.length > 0) {
        this.trafficData.forEach(domainData => {
          Object.keys(domainData.data).forEach(dateStr => {
            const date = new Date(dateStr)
            if (!latestDate || date > latestDate) {
              latestDate = date
            }
          })
        })

        if (latestDate) {
          this.selectedYear = latestDate.getFullYear()
          this.selectedMonth = latestDate.getMonth()

          // Establish fixed color mapping based on domains present in the latest date
          this.establishDomainColorMap(latestDate)

          this.calculateChartData()
        }
      }
    },
    establishDomainColorMap(latestDate) {
      // Clear existing map
      this.domainColorMap = {}

      // Get domains that have data for the latest month
      const latestYear = latestDate.getFullYear()
      const latestMonth = latestDate.getMonth()
      const startDate = new Date(latestYear, latestMonth, 1)
      const endDate = new Date(latestYear, latestMonth + 1, 0)

      const domainsWithData = []

      this.trafficData.forEach((domainData) => {
        let hasData = false
        Object.keys(domainData.data).forEach(dateStr => {
          const date = new Date(dateStr)
          if (date >= startDate && date <= endDate) {
            const value = domainData.data[dateStr]
            if (value !== null && value !== undefined && value > 0) {
              hasData = true
            }
          }
        })

        if (hasData) {
          domainsWithData.push(domainData.domainName)
        }
      })

      // Assign colors matching the line chart (by index in store domains)
      domainsWithData.forEach(domain => {
        const storeIndex = this.domains.indexOf(domain)
        this.domainColorMap[domain] = storeIndex !== -1 ? getColor(storeIndex) : getColor(0)
      })

      // Also assign colors to domains not in latest date for animation purposes
      this.trafficData.forEach((domainData) => {
        if (!this.domainColorMap[domainData.domainName]) {
          const storeIndex = this.domains.indexOf(domainData.domainName)
          this.domainColorMap[domainData.domainName] = storeIndex !== -1 ? getColor(storeIndex) : getColor(0)
        }
      })
    },
    onDateChange() {
      if (this.debugMode) console.log('[PieChart] Date changed to:', this.selectedYear, this.selectedMonth)

      // Destroy chart BEFORE calculating new data
      this.destroyChart()

      // Calculate new data
      this.calculateChartData()

      if (this.debugMode) console.log('[PieChart] Chart data calculated, length:', this.chartData.length)

      // Only render if we have data
      if (this.chartData.length > 0 && !this.isDestroying) {
        this.$nextTick(() => {
          this.$nextTick(() => {
            if (!this.isDestroying) {
              this.renderChart()
            }
          })
        })
      } else {
        // Hide canvas when no data
        if (this.$refs.pieCanvas) {
          this.$refs.pieCanvas.style.display = 'none'
        }
      }
    },
    calculateChartData() {
      if (!this.trafficData || this.trafficData.length === 0) {
        this.chartData = []
        return
      }

      const startDate = new Date(this.selectedYear, this.selectedMonth, 1)
      const endDate = new Date(this.selectedYear, this.selectedMonth + 1, 0)

      const domainTotals = {}

      // Calculate totals for the selected period
      this.trafficData.forEach((domainData) => {
        // Skip hidden domains
        if (this.hiddenDomains.includes(domainData.domainName)) {
          return
        }

        let total = 0
        Object.keys(domainData.data).forEach(dateStr => {
          const date = new Date(dateStr)
          if (date >= startDate && date <= endDate) {
            const value = domainData.data[dateStr]
            if (value !== null && value !== undefined) {
              total += value
            }
          }
        })

        if (total > 0) {
          // Use the fixed color from the map
          domainTotals[domainData.domainName] = {
            total,
            color: this.domainColorMap[domainData.domainName] || getColor(0)
          }
        }
      })

      this.chartData = Object.entries(domainTotals).map(([domain, data]) => ({
        domain,
        total: data.total,
        color: data.color,
      }))
    },
    renderChart() {
      if (this.debugMode) console.log('[PieChart] renderChart called')

      // Don't render if we're in the process of destroying
      if (this.isDestroying) {
        if (this.debugMode) console.log('[PieChart] Skipping render, chart is being destroyed')
        return
      }

      // Ensure canvas ref exists and is valid
      if (!this.$refs.pieCanvas) {
        console.error('[PieChart] Canvas ref not available')
        return
      }

      // Ensure we have data to render
      if (this.chartData.length === 0) {
        if (this.debugMode) console.log('[PieChart] No data to render')
        return
      }

      // Ensure any existing chart is destroyed first
      this.destroyChart()

      // Double-check we're not destroying after cleanup
      if (this.isDestroying) {
        if (this.debugMode) console.log('[PieChart] Skipping render after destroy')
        return
      }

      try {
        const canvas = this.$refs.pieCanvas
        if (!canvas) {
          console.error('[PieChart] Canvas element is null')
          return
        }

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          console.error('[PieChart] Cannot get canvas context')
          return
        }

        // Show canvas before creating chart
        canvas.style.display = 'block'
        if (this.debugMode) console.log('[PieChart] Creating new chart')
        this.createChart(ctx)
      } catch (e) {
        console.error('[PieChart] Error creating chart:', e)
        // Hide canvas on error
        if (this.$refs.pieCanvas) {
          this.$refs.pieCanvas.style.display = 'none'
        }
      }
    },
    createChart(ctx) {
      if (this.debugMode) console.log('[PieChart] Creating chart with', this.chartData.length, 'datasets')

      const labels = this.chartData.map(item => item.domain)
      const data = this.chartData.map(item => item.total)
      const colors = this.chartData.map(item => item.color)
      const totalTraffic = data.reduce((sum, val) => sum + val, 0)

      // Disable all animations to prevent issues
      this.chart = new Chart(ctx, {
        type: 'pie',
        data: {
          labels,
          datasets: [{
            data,
            backgroundColor: colors,
            borderColor: cssVar('--chart-segment-border'),
            borderWidth: 2,
            hoverBorderWidth: 3,
            hoverBorderColor: colors,
          }],
        },
        plugins: [ChartDataLabels], // Register datalabels plugin locally
        options: {
          responsive: true,
          maintainAspectRatio: true,
          animation: false, // Completely disable animations
          animations: {
            colors: false,
            x: false,
            y: false,
          },
          transitions: {
            active: {
              animation: {
                duration: 0
              }
            }
          },
          interaction: {
            intersect: true,
            mode: 'nearest',
          },
          plugins: {
            datalabels: {
              color: '#fff',
              font: {
                weight: 'bold',
                size: 14,
              },
              formatter: (value, ctx) => {
                // Check if this segment is hidden
                const meta = ctx.chart.getDatasetMeta(0)
                if (meta.data[ctx.dataIndex] && meta.data[ctx.dataIndex].hidden) {
                  return '' // Don't show label for hidden segments
                }

                // Calculate total of only visible segments
                let visibleTotal = 0
                ctx.dataset.data.forEach((val, idx) => {
                  if (!meta.data[idx] || !meta.data[idx].hidden) {
                    visibleTotal += val
                  }
                })

                const percentage = visibleTotal > 0 ? ((value * 100) / visibleTotal).toFixed(1) : '0'
                return percentage > 5 ? `${percentage}%` : '' // Only show if > 5%
              },
              display: true,
              anchor: 'center',
              align: 'center',
              offset: 0,
            },
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                generateLabels: (chart) => {
                  const data = chart.data
                  if (data.labels.length && data.datasets.length) {
                    const meta = chart.getDatasetMeta(0)
                    return data.labels.map((label, i) => {
                      const style = meta.controller.getStyle(i)
                      const isHidden = meta.data[i] && meta.data[i].hidden
                      return {
                        text: label,
                        fillStyle: isHidden ? cssVar('--chart-hidden') : style.backgroundColor,
                        strokeStyle: style.borderColor,
                        lineWidth: style.borderWidth,
                        hidden: isHidden,
                        index: i,
                        // Add custom properties for styling
                        fontColor: isHidden ? cssVar('--chart-hidden') : cssVar('--text-secondary2'),
                        textDecoration: isHidden ? 'line-through' : ''
                      }
                    })
                  }
                  return []
                },
                color: function(context) {
                  // Use the fontColor property if available
                  return context.fontColor || cssVar('--text-secondary2')
                },
                padding: 15,
                font: {
                  family: 'Manrope',
                  size: 12,
                },
                usePointStyle: true,
                pointStyle: 'circle',
              },
              onClick: (e, legendItem, legend) => {
                const index = legendItem.index
                const chart = legend.chart
                const meta = chart.getDatasetMeta(0)
                const domain = legendItem.text

                // Toggle visibility manually
                if (meta && meta.data && meta.data[index]) {
                  // Toggle the hidden state
                  meta.data[index].hidden = !meta.data[index].hidden

                  // Update hidden domains list
                  if (meta.data[index].hidden) {
                    if (!this.hiddenDomains.includes(domain)) {
                      this.hiddenDomains.push(domain)
                    }
                  } else {
                    this.hiddenDomains = this.hiddenDomains.filter(d => d !== domain)
                  }

                  // Update the chart
                  chart.update('none')
                }
              },
            },
            tooltip: {
              enabled: true,
              backgroundColor: cssVar('--bg-surface'),
              titleColor: cssVar('--text-primary'),
              bodyColor: cssVar('--text-primary'),
              borderColor: cssVar('--border-color-2'),
              borderWidth: 1,
              padding: 12,
              cornerRadius: 7,
              displayColors: false,
              callbacks: {
                title: () => '', // Hide redundant title to avoid duplication
                label: (context) => {
                  const value = context.parsed || 0
                  const formattedValue = nFormatter(value)

                  // Create informative tooltip with visits and share percentage
                  const domainName = context.label
                  const monthYear = `${this.monthNames[this.selectedMonth]} ${this.selectedYear}`

                  // Calculate percentage based on visible segments only
                  const meta = context.chart.getDatasetMeta(0)
                  let visibleTotal = 0
                  context.dataset.data.forEach((val, idx) => {
                    if (!meta.data[idx] || !meta.data[idx].hidden) {
                      visibleTotal += val
                    }
                  })
                  const percentage = visibleTotal > 0 ? ((value * 100) / visibleTotal).toFixed(1) : '0'

                  return [
                    domainName,
                    monthYear,
                    `Visits: ${formattedValue}`,
                    `Market Share: ${percentage}%`
                  ]
                },
              },
            },
          },
          onHover: (event, activeElements) => {
            event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default'
          },
        },
      })

      if (this.debugMode) console.log('[PieChart] Chart created successfully')
    },
  },
  mounted() {
    // при смене темы перекрашиваем границы секторов, легенду и тултип
    this.detachThemeListener = onThemeChange(() => this.applyThemeColors())
  },
  beforeUnmount() {
    if (this.detachThemeListener) this.detachThemeListener()
    this.destroyChart()
  },
}
</script>

<style scoped>
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--overlay-color);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background: var(--bg-surface);
  border-radius: 16px;
  padding: 32px;
  min-width: 500px;
  max-width: 700px;
  position: relative;
  box-shadow: var(--shadow-strong);
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.popup-title {
  font-size: 24px;
  font-weight: 600;
  color: var(--text-primary);
}

.close-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.close-icon:hover {
  opacity: 1;
}

.controls-container {
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.controls-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.animation-button {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--bg-surface-2);
  border: 2px solid var(--border-color-3);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.animation-button:hover:not(:disabled) {
  background: var(--main-color);
  border-color: var(--main-color);
  transform: scale(1.1);
}

.animation-button:hover:not(:disabled) img {
  filter: brightness(0) invert(1);
}

.animation-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.animation-button img {
  width: 24px;
  height: 24px;
  transition: filter 0.2s ease;
}

.speed-button {
  padding: 8px 16px;
  border-radius: 8px;
  background: var(--bg-surface-2);
  border: 2px solid var(--border-color-3);
  color: var(--main-color);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Manrope', sans-serif;
}

.speed-button:hover {
  background: var(--main-color);
  border-color: var(--main-color);
  color: white;
  transform: scale(1.05);
}

.date-display {
  flex: 1;
}

.animation-date {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  padding: 8px 16px;
  background: var(--bg-surface-2);
  border-radius: 8px;
  border: 1px solid var(--border-color-3);
  display: inline-block;
}

.date-selector {
  display: flex;
  gap: 12px;
  margin-left: auto;
}

.year-select,
.month-select {
  padding: 8px 12px;
  border: 1px solid var(--border-color-3);
  border-radius: 8px;
  background: var(--bg-surface-2);
  color: var(--text-primary);
  font-family: 'Manrope', sans-serif;
  font-size: 14px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s;
}

.year-select:hover,
.month-select:hover,
.year-select:focus,
.month-select:focus {
  border-color: var(--main-color);
}

.chart-container {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-data {
  text-align: center;
  color: var(--text-secondary2);
  padding: 40px;
  font-size: 16px;
}

@media (max-width: 768px) {
  .popup-content {
    min-width: auto;
    width: 90%;
    max-width: 90vw;
    padding: 24px;
  }

  .popup-title {
    font-size: 20px;
  }

  .chart-container {
    min-height: 300px;
  }
}
</style>
